import { ref, watch, computed, onMounted } from "@vue/composition-api";
import Highcharts from "highcharts";
import store from "@/store";
import { formatDate, title } from "@core/utils/filter";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
export default function useUsersList() {
  // Use toast
  const toast = useToast();
  const { t } = useI18nUtils();
  const refUserListTable = ref(null);

  // Table Handlers
  const tableColumns = computed(() => [
    { key: "username", label: t("User Name"), sortable: true },
    { key: "mail", label: t("Account"), sortable: true },
    { key: "coursename", label: t("Course Name"), sortable: false },
    { key: "coursestatus", label: t("Course status"), sortable: false },
    { key: "courseEndDate", label: t("Completed Date"), sortable: false },
    { key: "examname", label: t("Exam Name"), sortable: true },
    { key: "examstatus", label: t("Exam Status"), sortable: true },
    { key: "examStartDate", label: t("Exam Start Date") },
    { key: "userexamid", label: t("Detail") },
  ]);
  const perPage = ref(25);
  const totalUsers = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100, 500];
  const searchQuery = ref("");
  const debouncedSearchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  let searchDebounceTimer = null;
  const roleFilter = ref(null);
  const planFilter = ref(null);
  const statusFilter = ref(null);
  const items = ref();
  
  // New filter refs for server-side filtering
  const courseNameFilter = ref(null);
  const departmentNameFilter = ref(null);
  const examStatusFilter = ref(null);
  const courseStatusFilter = ref(null);
  
  // Dropdown options
  const courseNameOptions = ref([]);
  const departmentNameOptions = ref([]);
  const unitFilter = ref(null);
  const subunitFilter = ref(null);
  const unitOptions = ref([]);
  const subunitOptions = ref([]);
  const courseStatusOptions = computed(() => [
    { label: t("Completed"), value: true },
    { label: t("Unfinished"), value: false },
  ]);
  
  // Export loading state
  const isExporting = ref(false);
  
  // Pagination from API
  const totalPages = ref(0);
  const courseStatusData = ref([
    { name: "Completed", y: 0, color: "#55DD92" },
    { name: "Unfinished", y: 0, color: "#B4B9BF" },
  ]);

  const examStatusData = ref([
    { name: "Not Started", y: 0, color: "#B4B9BF" },
    { name: "Success", y: 0, color: "#55DD92" },
    { name: "Failed", y: 0, color: "#FF3507FF" },
  ]);

  const courseChart = ref(null);
  const statusChart = ref(null);
  
  // 2. METHODS
  const updateChartsWithCurrentPage = (pageData) => {
    const counts = {
      completed: 0,
      unfinished: 0,
      notStarted: 0,
      success: 0,
      failed: 0,
    };
    
    if (pageData && Array.isArray(pageData)) {
      pageData.forEach((item) => {
        item.coursestatus ? counts.completed++ : counts.unfinished++;
        if (item.examstatus === 200) counts.failed++;
        else if (item.examstatus === 100) counts.success++;
        else counts.notStarted++;
      });
    }

    courseStatusData.value = [
      { name: "Completed", y: counts.completed, color: "#55DD92" },
      { name: "Unfinished", y: counts.unfinished, color: "#B4B9BF" },
    ];

    examStatusData.value = [
      { name: "Not Started", y: counts.notStarted, color: "#B4B9BF" },
      { name: "Success", y: counts.success, color: "#55DD92" },
      { name: "Failed", y: counts.failed, color: "#FF3507FF" },
    ];
    
    // Update charts if they exist
    if (courseChart.value) {
      courseChart.value.series[0].setData(courseStatusData.value);
    }
    if (statusChart.value) {
      statusChart.value.series[0].setData(examStatusData.value);
    }
  };
  // 3. CHART INITIALIZATION
  const initCharts = () => {
    courseChart.value = Highcharts.chart("course-status-chart", {
      chart: { type: "pie" },
      title: { text: "Course Status" },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
      },
      plotOptions: {
        pie: {
          dataLabels: { enabled: true, format: "{point.name}: {point.y}" },
          showInLegend: true,
        },
      },
      series: [
        {
          name: "Courses",
          data: courseStatusData.value,
        },
      ],
    });

    statusChart.value = Highcharts.chart("exam-status-chart", {
      colors: ["#B4B9BF", "#55DD92", "#FF3507FF"],
      chart: {
        type: "pie",
        custom: { label: "Count of Exams", total: totalUsers.value },
        events: {
          render() {
            const chart = this;

            if (chart.customLabel) chart.customLabel.destroy();

            chart.customLabel = chart.renderer
              .label(
                `${chart.options.chart.custom.label}<br/>Total: ${totalUsers.value}`
              )
              .css({
                color: "#000",
                textAnchor: "middle",
                fontSize: "14px",
              })
              .attr({
                x: chart.plotWidth / 2 + chart.plotLeft,
                y: chart.plotHeight / 2 + chart.plotTop,
              })
              .add();
          },
        },
      },
      accessibility: {
        point: {
          valueSuffix: "%",
        },
      },
      title: {
        text: "Exam Status",
      },
      subtitle: {
        text: "Members exams status ratio",
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.percentage:.0f}%</b>",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          name: "Percentage",
          colorByPoint: true,
          innerSize: "75%",
          data: examStatusData.value,
        },
      ],
    });
  };
  // 4. WATCHERS
  watch(
    courseStatusData,
    (newData) => {
      if (courseChart.value) {
        courseChart.value.series[0].setData(newData);
      }
    },
    { deep: true }
  );

  watch(
    examStatusData,
    (newData) => {
      if (statusChart.value) {
        statusChart.value.series[0].setData(newData);
      }
    },
    { deep: true }
  );
  watch(totalUsers, (newTotal) => {
    if (statusChart.value) {
      statusChart.value.update(
        {
          chart: {
            custom: {
              label: "Count of Exams",
              total: newTotal,
            },
          },
        },
        false
      );
      statusChart.value.redraw();
    }
  });

  // Debounced search query watcher
  watch(searchQuery, (newValue) => {
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // Set new timer for 300ms
    searchDebounceTimer = setTimeout(() => {
      debouncedSearchQuery.value = newValue;
    }, 500);
  });
  
  // Server-side filtering watchers
  // When filters change, reset to page 1 and fetch
  watch(debouncedSearchQuery, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });
  
  watch(courseNameFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });
  
  watch(departmentNameFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });
  
  watch(examStatusFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });

  watch(unitFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });

  watch(subunitFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });

  watch(courseStatusFilter, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
      fetchStatistics();
    } else {
      currentPage.value = 1;
    }
  });
  
  watch(perPage, () => {
    if (currentPage.value === 1) {
      fetchMemberRecords();
    } else {
      currentPage.value = 1;
    }
  });
  
  // Watch currentPage and fetch data
  watch(currentPage, () => {
    fetchMemberRecords();
  });

  const dataMeta = computed(() => {
    const from = perPage.value * (currentPage.value - 1) + 1;
    const to = Math.min(from + perPage.value - 1, totalUsers.value);
    return {
      from: totalUsers.value === 0 ? 0 : from,
      to: totalUsers.value === 0 ? 0 : to,
      of: totalUsers.value,
    };
  });

  const fetchMemberRecords = async () => {
    try {
      const params = {
        page: currentPage.value,
        limit: perPage.value,
      };
      
      if (debouncedSearchQuery.value) {
        params.search = debouncedSearchQuery.value;
      }
      if (courseNameFilter.value) {
        params.courseName = courseNameFilter.value;
      }
      if (departmentNameFilter.value) {
        params.departmentName = departmentNameFilter.value;
      }
      if (examStatusFilter.value !== null && examStatusFilter.value !== undefined) {
        params.examStatus = examStatusFilter.value;
      }
      if (unitFilter.value) {
        params.unit = unitFilter.value;
      }
      if (subunitFilter.value) {
        params.subunit = subunitFilter.value;
      }
      if (courseStatusFilter.value !== null && courseStatusFilter.value !== undefined) {
        params.courseStatus = courseStatusFilter.value;
      }
      
      const response = await store.dispatch(
        "app-members-learning/fetchMemberRecords",
        params
      );

      const { data, pagination } = response.data;
      
      items.value = data || [];
      totalUsers.value = pagination.totalRecords;
      totalPages.value = pagination.totalPages;

      return data;
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Error fetching courses list",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      throw error;
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = {};
      
      // Sadece filtreler, pagination YOK
      if (debouncedSearchQuery.value) {
        params.search = debouncedSearchQuery.value;
      }
      if (courseNameFilter.value) {
        params.courseName = courseNameFilter.value;
      }
      if (departmentNameFilter.value) {
        params.departmentName = departmentNameFilter.value;
      }
      if (examStatusFilter.value !== null && examStatusFilter.value !== undefined) {
        params.examStatus = examStatusFilter.value;
      }
      if (unitFilter.value) {
        params.unit = unitFilter.value;
      }
      if (subunitFilter.value) {
        params.subunit = subunitFilter.value;
      }
      if (courseStatusFilter.value !== null && courseStatusFilter.value !== undefined) {
        params.courseStatus = courseStatusFilter.value;
      }
      // startDate ve endDate varsa eklenebilir
      
      const response = await store.dispatch(
        "app-members-learning/fetchStatistics",
        params
      );

      const { courseStatus, examStatus } = response.data.data;
      
      // Update chart data from server response
      courseStatusData.value = [
        { name: "Completed", y: courseStatus.completed || 0, color: "#55DD92" },
        { name: "Unfinished", y: courseStatus.unfinished || 0, color: "#B4B9BF" },
      ];

      examStatusData.value = [
        { name: "Not Started", y: examStatus.notStarted || 0, color: "#B4B9BF" },
        { name: "Success", y: examStatus.success || 0, color: "#55DD92" },
        { name: "Failed", y: examStatus.failed || 0, color: "#FF3507FF" },
      ];
      
      // Update charts if they exist
      if (courseChart.value) {
        courseChart.value.series[0].setData(courseStatusData.value);
      }
      if (statusChart.value) {
        statusChart.value.series[0].setData(examStatusData.value);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  const loadDistinctFilters = async () => {
    try {
      const [coursesResp, deptsResp, unitsResp, subunitsResp] = await Promise.all([
        store.dispatch("app-members-learning/fetchDistinctCourseNames"),
        store.dispatch("app-members-learning/fetchDistinctDepartments"),
        store.dispatch("app-members-learning/fetchDistinctUnits"),
        store.dispatch("app-members-learning/fetchDistinctSubunits"),
      ]);
      
      // Map objects to string arrays for v-select
      const courses = (coursesResp.data.data || []).map(item => item.courseName).filter(Boolean);
      const departments = (deptsResp.data.data || []).map(item => item.departmentName).filter(Boolean);
      const units = (unitsResp.data.data || [])
        .map(item => item.unit || item.unitName || item.name || item.value)
        .filter(Boolean);
      const subunits = (subunitsResp.data.data || [])
        .map(item => item.subunit || item.subunitName || item.name || item.value)
        .filter(Boolean);
      
      courseNameOptions.value = courses;
      departmentNameOptions.value = departments;
      unitOptions.value = units;
      subunitOptions.value = subunits;
    } catch (error) {
      console.error("Failed to load filter options", error);
      // Fallback: try to get from first page
      try {
        const response = await store.dispatch("app-members-learning/fetchMemberRecords", {
          page: 1,
          limit: 100,
        });
        const allData = response.data.data || [];
        const courses = [...new Set(allData.map(item => item.coursename))].filter(Boolean);
        const departments = [...new Set(allData.map(item => item.departmentname))].filter(Boolean);
        const units = [...new Set(allData.map(item => item.unit || item.unitname))].filter(Boolean);
        const subunits = [...new Set(allData.map(item => item.subunit || item.subunitname))].filter(Boolean);
        
        courseNameOptions.value = courses;
        departmentNameOptions.value = departments;
        unitOptions.value = units;
        subunitOptions.value = subunits;
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  };

  const togglePendingExams = () => {
    if (examStatusFilter.value === 50) {
      // Already filtered, clear it
      examStatusFilter.value = null;
    } else {
      // Apply pending filter
      examStatusFilter.value = 50;
    }
  };

  onMounted(async () => {
    try {
      // Skip fetching if data already exists (keep-alive support)
      if (items.value && items.value.length > 0) {
        return;
      }

      await loadDistinctFilters();
      await fetchMemberRecords();
      await fetchStatistics();
      initCharts();
    } catch (error) {
      console.error("❌ Initialization failed:", error);
    }
  });
  const refetchData = () => {
    fetchMemberRecords();
  };

  const resolveUserRoleVariant = (role) => {
    if (role === "subscriber") return "primary";
    if (role === "author") return "warning";
    if (role === "maintainer") return "success";
    if (role === "editor") return "info";
    if (role === "admin") return "danger";
    return "primary";
  };

  const resolveUserRoleIcon = (role) => {
    if (role === "subscriber") return "UserIcon";
    if (role === "author") return "SettingsIcon";
    if (role === "maintainer") return "DatabaseIcon";
    if (role === "editor") return "Edit2Icon";
    if (role === "admin") return "ServerIcon";
    return "UserIcon";
  };

  const resolveCourseStatusVariant = (status) => {
    if (status) return "success";
    return "secondary";
  };
  const resolveCourseStatusText = (status) => {
    if (status) return "completed";
    return "unfinished";
  };
  const resolveExamStatusVariant = (status) => {
    if (status === 0 || status === 1 || status === 10) return "secondary";
    if (status === 50) return "warning";
    if (status === 100) return "success";
    if (status === 200) return "danger";
    return "dark";
  };
  const resolveExamStatusText = (status) => {
    if (status === 0 || status === 1 || status === 10) return "Not started";
    if (status === 50) return "Pending for examer check";
    if (status === 100) return "Success";
    if (status === 200) return "Failed";
    return "No exam defined";
  };

  const resolvePageroute = (coursemode) => {
    if (coursemode === "IN-PERSON") return "course-sessions";
    if (coursemode === "ONLINE") return "course-lessons";
    if (coursemode === "HYBRID") return "course-lessons";

    return "course-lessons";
  };
  const resolveCourseContent = (coursemode) => {
    if (coursemode === "IN-PERSON") return "Sessions";
    if (coursemode === "ONLINE") return "Lectures";
    if (coursemode === "HYBRID") return "Lectures";

    return "Lectures";
  };
  const exportListToExcel = async () => {
    if (isExporting.value) return; // Prevent multiple clicks
    
    isExporting.value = true;
    try {
      // Filtreleri topla
      const params = {};
      
      if (debouncedSearchQuery.value) {
        params.search = debouncedSearchQuery.value;
      }
      if (courseNameFilter.value) {
        params.courseName = courseNameFilter.value;
      }
      if (departmentNameFilter.value) {
        params.departmentName = departmentNameFilter.value;
      }
      if (examStatusFilter.value !== null && examStatusFilter.value !== undefined) {
        params.examStatus = examStatusFilter.value;
      }
      if (unitFilter.value) {
        params.unit = unitFilter.value;
      }
      if (subunitFilter.value) {
        params.subunit = subunitFilter.value;
      }
      if (courseStatusFilter.value !== null && courseStatusFilter.value !== undefined) {
        params.courseStatus = courseStatusFilter.value;
      }
      // startDate ve endDate varsa eklenebilir
      
      // Backend'den direkt Excel dosyasını indir
      const response = await store.dispatch("app-members-learning/fetchExportData", params);
      
      // Response zaten blob (Excel dosyası)
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Dosyayı indir
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Members Learning List ${formatDate(new Date())}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Error exporting data",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    fetchMemberRecords,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    totalPages,
    dataMeta,
    perPageOptions,
    searchQuery,
    debouncedSearchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    exportListToExcel,
    isExporting,
    items,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveCourseStatusVariant,
    resolveCourseStatusText,
    resolveExamStatusVariant,
    resolveExamStatusText,
    refetchData,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
    resolvePageroute,
    resolveCourseContent,
    
    // New server-side filters
    courseNameFilter,
    departmentNameFilter,
    examStatusFilter,
    courseStatusFilter,
    courseStatusOptions,
    courseNameOptions,
    departmentNameOptions,
    unitFilter,
    subunitFilter,
    unitOptions,
    subunitOptions,
    togglePendingExams,
    fetchStatistics,
    
    // Charts
    updateChartsWithCurrentPage,
    initCharts,
  };
}
